import React, { useEffect, useState } from "react";

import { useAction, useDatasets, useDevice, useModels } from "../../../../Api/newSockets.api";
import Select from "../../../../components/DataEntry/Select";
import DeviceMenu from "../../Shared/DeviceMenu";
import DevicesHeader from "../../../../components/Header/DevicesHeader";
import LoadingSpinner from "../../../../components/LoadingSpinner/LoadingSpinner";
import { savePathToLocalStorage } from "../../../../utils/helpers";
import CapturePreviewModal from "../../../../components/Modals/CapturePreviewModal";
import ChessboardCalibrate from "../../../../components/Annotation/ChessboardCalibrate";

import { ReactComponent as ArrowLeftIcon } from "../../../../assets/icons/arrow_left_rotate.svg";
import { ReactComponent as ArrowRightIcon } from "../../../../assets/icons/arrow_right_rotate.svg";
import { ReactComponent as CameraIcon } from "../../../../assets/icons/camera.svg";
import { ReactComponent as CalibrateIcon } from "../../../../assets/icons/calibrate.svg";

type Props = {
  match: any;
  deviceId: string;
  label: string;
  icon: any;
};

const Stream = (props: Props) => {
  savePathToLocalStorage();

  const [streamImg, setStreamImg] = useState(
    "https://upload.wikimedia.org/wikipedia/commons/b/b1/Loading_icon.gif?20151024034921"
  );

  const deviceId = props.match.params.deviceId;

  // Capture name modal state (collected before capturing, like the dashboard)
  const [captureModalOpen, setCaptureModalOpen] = useState(false);
  const [captureName, setCaptureName] = useState('');
  const [captureLoading, setCaptureLoading] = useState(false);

  // Preview modal state
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [capturedProcessedImageData, setCapturedProcessedImageData] = useState('');
  const [capturedOriginalImageData, setCapturedOriginalImageData] = useState('');
  const [capturedMetrics, setCapturedMetrics] = useState<any>({});
  const [confirmLoading, setConfirmLoading] = useState(false);

  // Chessboard calibration state
  const [chessboardCalibrationActive, setChessboardCalibrationActive] = useState(false);

  const { getDevice, rotateStream, cameraCapture, confirmCapture, setDeviceSettings, cameraCalibrate } = useDevice();
  const { models, searchModels, connected: modelsConnected } = useModels();
  const { searchDatasets, connected: datasetsConnected } = useDatasets();

  // Model selection state
  const [selectedModelId, setSelectedModelId] = useState('');

  const device = getDevice(parseInt(deviceId));
  const deviceData = device.device;
  const settings = deviceData?.settings ?? {};
  const calibration = settings?.calibration ?? settings;

  // Initialize stream URL and model
  useEffect(() => {
    if (device.device) {
      setStreamImg(
        device.device.settings.cameraStreamEndpoint +
        `?cb=${Math.random()}`
      );
      if (device.device.settings?.modelId != null) {
        setSelectedModelId(String(device.device.settings.modelId));
      }
    }
  }, [device.device]);

  // Fetch models when socket is connected
  useEffect(() => {
    if (modelsConnected) {
      searchModels('').catch(() => {});
    }
  }, [modelsConnected]);

  useEffect(() => {
    if (datasetsConnected) {
      searchDatasets('').catch(() => {});
    }
  }, [datasetsConnected]);

  const handleModelChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedModelId(e.target.value);
    setDeviceSettings(parseInt(deviceId), { modelId: e.target.value });
  };

  const rotateImage = (rotation: string) => {
    rotateStream(deviceId, rotation);
  };

  const handleCapture = async () => {
    if (captureLoading) return;
    setCaptureLoading(true);
    try {
      const response: any = await cameraCapture(
        deviceId,
        settings?.defaultDataset ?? null,
        captureName || 'untitled'
      );
      const processedData = response?.processedImage ?? response?.image;
      if (response && processedData) {
        setCapturedProcessedImageData(processedData);
        setCapturedOriginalImageData(response?.originalImage || '');
        setCapturedMetrics(response.metrics ?? {});
        setCaptureModalOpen(false);
        setCaptureLoading(false);
        setPreviewModalOpen(true);
      } else {
        console.error('Invalid capture response:', response);
        alert('Failed to capture image. Please try again.');
        setCaptureLoading(false);
      }
    } catch (error) {
      console.error('Capture error:', error);
      alert('Failed to capture image. Please try again.');
      setCaptureLoading(false);
    }
  };

  const runChessboardCalibration = () => {
    cameraCalibrate(deviceId, 0, 0, false);
    setChessboardCalibrationActive(true);
  };

  return deviceData ? (
    <>
      <div className="device-main main-content">
        <DevicesHeader device={deviceData} />
        <div className="stream-body-content">
          <div>
            <DeviceMenu typeId={deviceData.deviceTypes.id} />
          </div>
          <div className="stream-content">
            {/* Controls Bar */}
            <div className="stream-controls">
              {/* Rotate Controls */}
              <div className="control-group">
                <span className="control-label">Rotate</span>
                <div className="control-buttons">
                  <button
                    onClick={() => rotateImage("counterclockwise")}
                    className="control-btn"
                    title="Rotate counterclockwise"
                  >
                    <ArrowLeftIcon />
                  </button>
                  <button
                    onClick={() => rotateImage("clockwise")}
                    className="control-btn"
                    title="Rotate clockwise"
                  >
                    <ArrowRightIcon />
                  </button>
                </div>
              </div>

              {/* Capture Button */}
              <div className="control-group">
                <button
                  onClick={() => {
                    setCaptureName('');
                    setCaptureModalOpen(true);
                  }}
                  className="control-btn primary"
                  title="Capture image"
                >
                  <CameraIcon />
                  <span>Capture</span>
                </button>
              </div>

              {/* Chess Calibration Button */}
              <div className="control-group">
                <button
                  onClick={runChessboardCalibration}
                  className="control-btn"
                  title="Run chessboard calibration"
                >
                  <CalibrateIcon />
                  <span>Calibrate</span>
                </button>
              </div>

              {/* Model Selection */}
              <div className="control-group model-select">
                <span className="control-label">Model</span>
                <Select
                  placeholder="Select model"
                  options={
                    models?.map((m: any) => ({ id: String(m.id), name: m.name })) ||
                    []
                  }
                  value={selectedModelId}
                  onChange={handleModelChange}
                  className="select-classic"
                />
              </div>
            </div>

            {/* Camera Stream */}
            <div className="stream-view">
              <img src={streamImg} alt="Camera stream" />
            </div>

            {/* Calibration Info (if available) */}
            {calibration?.points && (
              <div className="calibration-status">
                <span className="status-badge calibrated">Calibrated</span>
                <span className="calibration-details">
                  {calibration.rows} x {calibration.cols} | {calibration.square_mm}mm
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Chessboard Calibration Modal */}
      {chessboardCalibrationActive && (
        <ChessboardCalibrate
          source={deviceData.settings?.cameraStreamEndpoint}
          deviceId={deviceData.id}
          setCalibrationActive={setChessboardCalibrationActive}
        />
      )}

      {/* Capture Name Modal (collect a name before capturing) */}
      {captureModalOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '20px',
            boxSizing: 'border-box',
          }}
        >
          <div
            style={{
              background: '#21282c',
              border: '2px solid #ff9800',
              borderRadius: '16px',
              padding: '2rem',
              maxWidth: '480px',
              width: '100%',
              boxShadow: '0 0 60px rgba(255, 152, 0, 0.4)',
            }}
          >
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <div style={{ fontSize: '2rem', background: 'rgba(255, 152, 0, 0.2)', padding: '0.5rem', borderRadius: '8px', border: '1px solid rgba(255, 152, 0, 0.4)' }}>📷</div>
                <div>
                  <h2 style={{ margin: 0, color: '#ff9800', fontSize: '1.5rem', fontWeight: 'bold', letterSpacing: '1px' }}>
                    {captureLoading ? 'CAPTURING...' : 'CAPTURE IMAGE'}
                  </h2>
                  <p style={{ margin: '0.25rem 0 0 0', color: '#6c757d', fontSize: '0.85rem', fontFamily: 'monospace' }}>
                    {captureLoading ? 'Processing image...' : `${deviceData?.name ?? 'Camera'} • Device ${deviceId}`}
                  </p>
                </div>
              </div>
              <button
                disabled={captureLoading}
                onClick={() => !captureLoading && setCaptureModalOpen(false)}
                style={{
                  background: 'transparent',
                  border: '2px solid #dc3545',
                  borderRadius: '8px',
                  color: '#dc3545',
                  fontSize: '1.5rem',
                  width: '40px',
                  height: '40px',
                  cursor: captureLoading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: captureLoading ? 0.5 : 1,
                }}
              >
                ✕
              </button>
            </div>

            {/* Name Input */}
            <div style={{ marginBottom: '1.5rem', display: captureLoading ? 'none' : 'block' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: '#ff9800', fontSize: '0.9rem', fontWeight: 'bold', textTransform: 'uppercase' }}>
                Capture Name
              </label>
              <input
                type="text"
                value={captureName}
                onChange={(e) => setCaptureName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !captureLoading) handleCapture(); }}
                placeholder="Enter a name for this capture..."
                disabled={captureLoading}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: '#242424',
                  border: '2px solid rgba(255, 152, 0, 0.3)',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '1rem',
                  fontFamily: 'monospace',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
                autoFocus
              />
            </div>

            {/* Buttons */}
            <div style={{ display: captureLoading ? 'none' : 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                disabled={captureLoading}
                onClick={() => {
                  if (captureLoading) return;
                  setCaptureModalOpen(false);
                  setCaptureName('');
                }}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: 'transparent',
                  border: '2px solid #6c757d',
                  borderRadius: '8px',
                  color: '#6c757d',
                  fontSize: '0.9rem',
                  fontWeight: 'bold',
                  textTransform: 'uppercase',
                  cursor: captureLoading ? 'not-allowed' : 'pointer',
                  opacity: captureLoading ? 0.5 : 1,
                }}
              >
                Cancel
              </button>
              <button
                disabled={captureLoading}
                onClick={handleCapture}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: 'linear-gradient(135deg, rgba(255, 152, 0, 0.4) 0%, rgba(255, 152, 0, 0.2) 100%)',
                  border: '2px solid #ff9800',
                  borderRadius: '8px',
                  color: '#ff9800',
                  fontSize: '0.9rem',
                  fontWeight: 'bold',
                  textTransform: 'uppercase',
                  cursor: captureLoading ? 'not-allowed' : 'pointer',
                  boxShadow: '0 0 20px rgba(255, 152, 0, 0.4)',
                  opacity: captureLoading ? 0.7 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                }}
              >
                📷 CAPTURE
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Capture Preview Modal */}
      {previewModalOpen && (
        <CapturePreviewModal
          visible={previewModalOpen}
          imageData={capturedProcessedImageData}
          metrics={capturedMetrics}
          loading={confirmLoading}
          onConfirm={async () => {
            if (confirmLoading) return;

            setConfirmLoading(true);

            const nameToSave = captureName || 'untitled';

            setTimeout(() => {
              setPreviewModalOpen(false);
              setCaptureName('');
              setCapturedProcessedImageData('');
              setCapturedOriginalImageData('');
              setCapturedMetrics({});
              setConfirmLoading(false);
            }, 100);

            try {
              await confirmCapture(
                capturedProcessedImageData,
                capturedOriginalImageData,
                capturedMetrics,
                deviceId,
                settings?.defaultDataset ?? null,
                nameToSave
              );
              console.log('Image saved successfully');
            } catch (error) {
              console.error('Failed to save image:', error);
            }
          }}
          onCancel={() => {
            setPreviewModalOpen(false);
            setCaptureName('');
            setCapturedProcessedImageData('');
            setCapturedOriginalImageData('');
            setCapturedMetrics({});
          }}
        />
      )}
    </>
  ) : (
    <LoadingSpinner />
  );
};

export default Stream;
